import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAllChannelStats } from '../services/youtubeApi';

// Cache for storing channel stats
const statsCache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

export const useYouTubeStats = () => {
  const [channelStats, setChannelStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const abortControllerRef = useRef(null);

  const fetchStats = useCallback(async () => {
    // Check cache first
    const cached = statsCache.get('channelStats');
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setChannelStats(cached.data);
      setLoading(false);
      setUsingFallback(true);
      return;
    }

    try {
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setLoading(true);
      setError(null);
      
      const stats = await fetchAllChannelStats();
      
      // Only update if the request wasn't cancelled
      if (!controller.signal.aborted) {
        // Cache the results
        statsCache.set('channelStats', {
          data: stats,
          timestamp: Date.now()
        });
        
        setUsingFallback(true);
        setError(null);
        setChannelStats(stats);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError('Failed to load channel statistics');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();

    // Refresh stats every 10 minutes instead of 5
    const interval = setInterval(fetchStats, CACHE_DURATION);

    return () => {
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchStats]);

  return { channelStats, loading, error, usingFallback, refreshStats: fetchStats };
};
